import { useState, useEffect } from 'react';
import { Card, Typography, Empty, Spin, Alert, Tabs } from 'antd';
import { CalendarOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { CountryFlag } from '../../components/Common/CountryFlag';
import { useEvent } from '../../hooks/useEvent';
import { usePermissions } from '../../hooks/usePermissions';
import { useTheme } from '../../contexts/useTheme';
import { CircuitPreview } from '../../components/Map/CircuitPreview';
import { climateRecapAnalyticsTabItems } from '../../components/ClimateRecap/climateRecapAnalyticsTabItems';
import { circuitsService, type CircuitWithGeoJSON } from '../../api/circuitsService';
import { formatDateRange } from '../../utils/dateUtils';
import './ClimateRecap.css';

const { Title, Text, Paragraph } = Typography;

export const ClimateRecapPage = () => {
  const { selectedEvent } = useEvent();
  const { isRoot } = usePermissions();
  const { theme } = useTheme();
  const [circuitData, setCircuitData] = useState<CircuitWithGeoJSON | null>(null);
  const [loadingCircuit, setLoadingCircuit] = useState(false);

  // Load circuit data when event changes
  useEffect(() => {
    if (selectedEvent?.circuit_id) {
      setLoadingCircuit(true);
      circuitsService.getCircuitById(selectedEvent.circuit_id, selectedEvent.circuit_geojson_id)
        .then((data: CircuitWithGeoJSON) => setCircuitData(data))
        .catch((err: Error) => {
          console.error('Failed to load circuit data:', err);
          setCircuitData(null);
        })
        .finally(() => setLoadingCircuit(false));
    } else {
      setCircuitData(null);
    }
  }, [selectedEvent?.circuit_id, selectedEvent?.circuit_geojson_id]);


  const formatOverviewSummary = (text: string) => {
    // Replace \r\n and \n with proper line breaks
    return text.split(/\\n|\\r\\n|\r\n|\n/).map((line, index) => (
      <Paragraph key={index} style={{ marginBottom: line.trim() === '' ? 8 : 4 }}>
        {line.trim() || '\u00A0'}
      </Paragraph>
    ));
  };

  if (!selectedEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Scrollable Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  No event selected. Please select an event from the header to view its climate recap.
                </span>
              }
            />
          </Card>
        </div>
      </div>
    );
  }

  if (selectedEvent.demo && !isRoot) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="This event is not available. Please select a different event."
            />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Scrollable Content */}
      <div className="climate-recap-page" style={{ flex: 1, overflowY: 'auto', padding: 0 }}>
        {/* Event Header - Race View */}
        <Card className="event-header-card" variant="borderless">
          <div className="event-header-content">
            <div className="event-header-left">
              <div className="event-flag">
                <CountryFlag
                  country={selectedEvent.circuit.country}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '4px',
                    objectFit: 'cover',
                  }}
                  title={selectedEvent.circuit.country}
                />
              </div>
              
              <div className="event-details">
                <div className="event-main-info">
                  <Title level={2} className="event-name">
                    {selectedEvent.name}
                  </Title>
                  <div className="event-meta">
                    <span className="event-meta-item">
                      <CalendarOutlined />
                      <Text>{selectedEvent.season_year}</Text>
                    </span>
                    <span className="event-meta-item">
                      <Text type="secondary">
                        {formatDateRange(selectedEvent.start_date, selectedEvent.end_date)}
                      </Text>
                    </span>
                  </div>
                </div>
                
                <div className="event-location">
                  <EnvironmentOutlined />
                  <Text strong>{selectedEvent.circuit.display_name}</Text>
                </div>
              </div>
            </div>

            <div className="event-circuit-preview">
              {loadingCircuit ? (
                <div className="circuit-loading">
                  <Spin size="small" />
                </div>
              ) : (
                <CircuitPreview
                  circuitId={selectedEvent.circuit_id}
                  theme={theme}
                  stations={selectedEvent.stations}
                  circuitData={circuitData || undefined}
                />
              )}
            </div>
          </div>
        </Card>

        {/* Tabs Section */}
        <Card className="climate-tabs-card" style={{ marginTop: 0 }}>
          <Tabs
            defaultActiveKey="overview"
            items={[
              {
                key: 'overview',
                label: 'Overview',
                children: (
                  <div>
          {selectedEvent.overview_summary ? (
            <div className="overview-content">
              {formatOverviewSummary(selectedEvent.overview_summary)}
            </div>
          ) : (
            <Alert
              title="No climate overview available"
              description="Climate overview data is not available for this event."
              type="info"
              showIcon
            />
          )}
                  </div>
                ),
              },
              ...climateRecapAnalyticsTabItems(selectedEvent.circuit_id, {
                demo: selectedEvent.demo,
                legacy: selectedEvent.legacy,
                stations: selectedEvent.stations,
                circuitTimeZone: selectedEvent.circuit.time_zone,
              }),
            ]}
          />
        </Card>
      </div>
    </div>
  );
};

